import React, { useState, useEffect } from 'react';
import Mapa from './Mapa';
import Perfil from './Perfil';

const API = process.env.REACT_APP_API_URL;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function LinkMaps({ latitud, longitud, color }) {
  if (!latitud || !longitud) return null;
  const url = 'https://www.google.com/maps?q=' + latitud + ',' + longitud;
  return (
    <div style={{ fontSize: 10, marginTop: 3 }}>
      <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: color, textDecoration: 'none' }}>
        Ver en Google Maps
      </a>
    </div>
  );
}

export default function VistaCliente({ usuario, token, onLogout, darkMode, onToggleDark, dm, onNuevoPredio }) {
  const isMobile = useIsMobile();
  const [predios, setPredios] = useState([]);
  const [predioSeleccionado, setPredioSeleccionado] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [todosLosFocos, setTodosLosFocos] = useState([]);
  const [focosCercanos, setFocosCercanos] = useState([]);
  const [radio, setRadio] = useState(10);
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(false);
  const [rangoStats, setRangoStats] = useState('dia');
  const [verPerfil, setVerPerfil] = useState(false);

  const headers = { Authorization: 'Bearer ' + token };

  const COLORES_NIVEL = {
    extremo: { bg: darkMode ? '#2a0a0a' : '#fff5f5', border: '#c53030', texto: darkMode ? '#ff8080' : '#c53030', label: 'EXTREMO' },
    alto: { bg: darkMode ? '#2a1500' : '#fff8f0', border: '#dd6b20', texto: darkMode ? '#ffaa60' : '#c05621', label: 'ALTO' },
    moderado: { bg: darkMode ? '#1a2a0a' : '#f5faee', border: '#d69e2e', texto: darkMode ? '#ffd060' : '#b7791f', label: 'MODERADO' },
    bajo: { bg: darkMode ? '#0f1f0f' : '#f5f8f8', border: '#8fa3a0', texto: darkMode ? '#8fa3a0' : '#5a7a78', label: 'BAJO' }
  };

  useEffect(() => { cargarDatos(); }, []);
  useEffect(() => { if (predioSeleccionado) cargarFocosCercanos(); }, [predioSeleccionado, radio]);
  useEffect(() => { cargarStats(rangoStats); }, [rangoStats]);

  const cargarDatos = async () => {
    try {
      const [rPredios, rAlertas, rFocos] = await Promise.all([
        fetch(API + '/api/cliente/predios', { headers }).then(r => r.json()),
        fetch(API + '/api/cliente/alertas', { headers }).then(r => r.json()),
        fetch(API + '/api/focos?horas=24').then(r => r.json()),
      ]);
      // Igual que en cargarStats: un fallo puntual (rate limit, red) no debe vaciar lo que
      // ya se estaba mostrando — solo actualizar cuando la respuesta realmente vino ok.
      if (rPredios.ok) {
        setPredios(rPredios.predios || []);
        if (rPredios.predios && rPredios.predios.length > 0) setPredioSeleccionado(prev => prev || rPredios.predios[0]);
      }
      if (rAlertas.ok) setAlertas(rAlertas.alertas || []);
      if (rFocos.ok) setTodosLosFocos(rFocos.focos || []);
    } catch (e) { console.error(e); }
  };

  const cargarStats = async (rango) => {
    try {
      const r = await fetch(API + '/api/focos/stats?rango=' + rango).then(r => r.json());
      // Un fallo (rate limit, red, etc.) no debe borrar lo que ya se estaba mostrando —
      // antes cualquier error dejaba stats en null y la sección entera desaparecía justo
      // mientras el usuario cambiaba de filtro. Pero SÍ hay que avisar que quedó
      // desactualizado — antes fallaba en silencio y el usuario veía el mismo número de
      // siempre al cambiar de filtro, como si el filtro no hiciera nada.
      if (r.ok && r.stats) { setStats(r.stats); setStatsError(false); }
      else setStatsError(true);
    } catch (e) { console.error(e); setStatsError(true); }
  };

  const cargarFocosCercanos = async () => {
    if (!predioSeleccionado) return;
    try {
      const r = await fetch(API + '/api/cliente/focos-cercanos?predio_id=' + predioSeleccionado.id + '&radio=' + radio, { headers }).then(r => r.json());
      if (r.ok) setFocosCercanos(r.focos || []);
    } catch (e) { console.error(e); }
  };

  const focoMasCercano = focosCercanos.length > 0 ? focosCercanos[0] : null;
  const nivelActual = focoMasCercano ? COLORES_NIVEL[focoMasCercano.nivel_riesgo] : null;

  const RANGOS_STATS = [
    { id: 'hora', label: '1h', descripcion: 'última hora' },
    { id: 'dia', label: 'Hoy', descripcion: 'hoy' },
    { id: 'semana', label: 'Semana', descripcion: 'la semana' },
    { id: 'mes', label: 'Mes', descripcion: 'el mes' },
    { id: 'historico', label: 'Histórico', descripcion: 'histórico' },
  ];
  const descripcionRango = RANGOS_STATS.find(r => r.id === rangoStats)?.descripcion || 'hoy';

  const statsNacional = stats ? [
    { label: `Focos (${descripcionRango})`, valor: stats.total, color: dm.accent },
    { label: 'Extremos', valor: stats.extremos, color: '#c53030' },
    { label: 'Altos', valor: stats.altos, color: '#dd6b20' },
  ] : [];
  const statsDesde = stats?.desde ? new Date(stats.desde).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

  return (
    <div style={{ minHeight: '100vh', background: dm.bg, fontFamily: 'Inter, sans-serif', display: 'flex', flexDirection: 'column' }}>

      {verPerfil && (
        <Perfil
          usuario={usuario}
          token={token}
          onClose={() => setVerPerfil(false)}
          dm={dm}
          predios={predios}
          onPredioAgregado={(predio) => { setVerPerfil(false); onNuevoPredio?.(predio); }}
          onPredioEliminado={() => cargarDatos()}
        />
      )}

      <div style={{ background: dm.card, padding: isMobile ? '10px 12px' : '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: dm.shadow, borderBottom: '1px solid ' + dm.border, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src={require(darkMode ? '../assets/logo-header-white.png' : '../assets/logo-header.png')} alt="Montaraz" style={{ height: isMobile ? 44 : 60, objectFit: 'contain' }} />
          {!isMobile && <span style={{ background: dm.accentLight, color: dm.badge, fontSize: 11, padding: '2px 8px', borderRadius: 20, border: '1px solid ' + dm.badge }}>En vivo</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12 }}>
          {!isMobile && <span style={{ fontSize: 13, color: dm.text }}>Hola, {usuario.nombre}</span>}
          <span style={{ fontSize: 11, background: dm.accent, color: 'white', padding: '2px 8px', borderRadius: 20 }}>{usuario.plan}</span>
          <button onClick={() => setVerPerfil(true)} style={{ fontSize: 11, color: dm.text, background: dm.accentLight, border: '1px solid ' + dm.border, borderRadius: 20, padding: '3px 10px', cursor: 'pointer' }}>
            Mi perfil
          </button>
          <button onClick={onToggleDark} aria-label={darkMode ? 'Modo claro' : 'Modo oscuro'} title={darkMode ? 'Modo claro' : 'Modo oscuro'} style={{
            background: dm.accentLight, border: '1px solid ' + dm.border, borderRadius: '50%',
            width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1,
          }}>
            {darkMode ? '☀️' : '🌙'}
          </button>
          <button onClick={onLogout} style={{ fontSize: 11, color: dm.muted, background: 'none', border: 'none', cursor: 'pointer' }}>Salir</button>
        </div>
      </div>

      {usuario.plan === 'trial' && (
        <div style={{ background: '#fffbeb', borderBottom: '1px solid #f6d860', padding: isMobile ? '10px 14px' : '10px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', flexShrink: 0 }}>
          <div style={{ fontSize: 13, color: '#7d5f00' }}>
            <strong>Modo exploración</strong> — Estás viendo el dashboard en tiempo real, pero las alertas WhatsApp están desactivadas.
          </div>
          {predioSeleccionado && (
            <button
              onClick={() => onNuevoPredio?.(predioSeleccionado)}
              style={{ background: '#3d6b2a', color: 'white', border: 'none', borderRadius: 999, padding: '7px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Activar alertas WhatsApp →
            </button>
          )}
        </div>
      )}

      <div style={{ padding: isMobile ? 10 : 16, flex: 1, display: 'flex', flexDirection: 'column', overflow: isMobile ? 'auto' : 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 340px', gap: 12, flex: isMobile ? 'none' : 1, minHeight: 0 }}>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>
            {predios.length > 0 && (
              <div style={{ background: dm.card, borderRadius: 12, padding: '10px 12px', boxShadow: dm.shadow, border: '1px solid ' + dm.border, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
                {predios.length > 1 && predios.map(p => (
                  <button key={p.id} onClick={() => setPredioSeleccionado(p)} style={{
                    padding: '4px 12px', borderRadius: 20, border: '1px solid',
                    borderColor: predioSeleccionado && predioSeleccionado.id === p.id ? dm.accent : dm.border,
                    background: predioSeleccionado && predioSeleccionado.id === p.id ? dm.accent : 'transparent',
                    color: predioSeleccionado && predioSeleccionado.id === p.id ? 'white' : dm.text,
                    cursor: 'pointer', fontSize: 12, fontWeight: 600
                  }}>
                    {p.nombre}
                  </button>
                ))}
                {predios.length === 1 && (
                  <span style={{ fontSize: 12, fontWeight: 600, color: dm.text }}>{predioSeleccionado ? predioSeleccionado.nombre : ''}</span>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: dm.muted }}>Radio:</span>
                  {[3, 10, 15].map(r => (
                    <button key={r} onClick={() => setRadio(r)} style={{
                      padding: '2px 8px', borderRadius: 20, border: '1px solid',
                      borderColor: radio === r ? dm.accent : dm.border,
                      background: radio === r ? dm.accentLight : 'transparent',
                      color: radio === r ? dm.accent : dm.muted,
                      cursor: 'pointer', fontSize: 11, fontWeight: radio === r ? 600 : 400
                    }}>{r}</button>
                  ))}
                  <span style={{ fontSize: 10, color: dm.muted }}>{focosCercanos.length} focos</span>
                </div>
              </div>
            )}
            <div style={{ height: isMobile ? '50vh' : undefined, flex: isMobile ? 'none' : 1, borderRadius: 12, overflow: 'hidden', minHeight: 0 }}>
              <Mapa focos={todosLosFocos} predios={predios} radio={radio} darkMode={darkMode} predioSeleccionado={predioSeleccionado} />
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, overflowY: isMobile ? 'visible' : 'auto' }}>

            <div style={{
              background: nivelActual ? nivelActual.bg : dm.card,
              borderRadius: 12,
              padding: '14px',
              boxShadow: dm.shadow,
              border: '1px solid ' + (nivelActual ? nivelActual.border : dm.border),
              borderLeft: '4px solid ' + (nivelActual ? nivelActual.border : dm.accent),
              flexShrink: 0
            }}>
              <div style={{ fontSize: 10, color: dm.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '1px' }}>Estado del predio</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: dm.text, marginBottom: 8 }}>{predioSeleccionado ? predioSeleccionado.nombre : 'Sin predio'}</div>
              {focoMasCercano ? (
                <div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: nivelActual.texto, marginBottom: 4 }}>{nivelActual.label}</div>
                  <div style={{ fontSize: 12, color: nivelActual.texto, marginBottom: 8 }}>
                    Foco mas cercano a {parseFloat(focoMasCercano.distancia_km).toFixed(1)} km
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    {[
                      { label: 'Intensidad', valor: parseFloat(focoMasCercano.frp).toFixed(0) + ' MW' },
                      { label: 'Fuente', valor: focoMasCercano.fuente },
                      { label: 'Temperatura', valor: focoMasCercano.temperatura ? focoMasCercano.temperatura + 'C' : '—' },
                      { label: 'Viento', valor: focoMasCercano.viento_kmh ? focoMasCercano.viento_kmh + ' km/h' : '—' },
                    ].map(d => (
                      <div key={d.label} style={{ background: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.6)', borderRadius: 8, padding: '6px 8px' }}>
                        <div style={{ fontSize: 10, color: dm.muted }}>{d.label}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: dm.text }}>{d.valor}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '6px 0' }}>
                  <div style={{ fontSize: 26, marginBottom: 4 }}>✅</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: dm.accent }}>Sin focos en radio</div>
                  <div style={{ fontSize: 11, color: dm.muted, marginTop: 4 }}>No hay actividad en {radio} km</div>
                </div>
              )}
            </div>

            {stats && (
              <div style={{ background: dm.card, borderRadius: 12, padding: '12px', boxShadow: dm.shadow, border: '1px solid ' + dm.border, flexShrink: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 10, color: dm.muted, textTransform: 'uppercase', letterSpacing: '1px' }}>Situación nacional</div>
                  <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {RANGOS_STATS.map(r => (
                      <button key={r.id} onClick={() => setRangoStats(r.id)} style={{
                        fontSize: 10, padding: '3px 7px', borderRadius: 6, cursor: 'pointer',
                        fontFamily: 'Inter, sans-serif', fontWeight: 600,
                        border: '1px solid ' + (rangoStats === r.id ? dm.accent : dm.border),
                        background: rangoStats === r.id ? dm.accent : 'transparent',
                        color: rangoStats === r.id ? 'white' : dm.muted,
                      }}>
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                  {statsNacional.map(s => (
                    <div key={s.label} style={{ background: darkMode ? 'rgba(0,0,0,0.2)' : dm.accentLight, borderRadius: 8, padding: '6px 10px' }}>
                      <div style={{ fontSize: 10, color: dm.muted }}>{s.label}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.valor}</div>
                    </div>
                  ))}
                </div>
                {statsError && (
                  <div style={{ fontSize: 10, color: '#c05621', marginTop: 6 }}>
                    ⚠ No se pudo actualizar — mostrando el último dato disponible.
                  </div>
                )}
                {!statsError && rangoStats === 'historico' && statsDesde && (
                  <div style={{ fontSize: 10, color: dm.muted, marginTop: 6 }}>
                    Datos desde {statsDesde}
                  </div>
                )}
              </div>
            )}

            <div style={{ background: dm.card, borderRadius: 12, padding: 12, boxShadow: dm.shadow, border: '1px solid ' + dm.border, flex: isMobile ? 'none' : 1, overflowY: 'auto', minHeight: 0 }}>
              <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13, color: dm.text }}>Mis alertas</div>
              <div style={{ fontSize: 10, color: dm.muted, marginBottom: 8 }}>Personalizadas para tus predios</div>
              {alertas.length === 0 ? (
                <div style={{ color: dm.muted, fontSize: 12, textAlign: 'center', padding: '10px 0' }}>Sin alertas activas</div>
              ) : alertas.slice(0, 8).map(a => {
                const c = COLORES_NIVEL[a.nivel_riesgo] || COLORES_NIVEL.bajo;
                return (
                  <div key={a.id} style={{ background: c.bg, borderLeft: '3px solid ' + c.border, borderRadius: 8, padding: '7px 10px', marginBottom: 6 }}>
                    <div style={{ fontWeight: 600, color: c.texto, fontSize: 11 }}>{c.label} — {a.predio_nombre}</div>
                    <div style={{ fontSize: 10, color: dm.subtext, marginTop: 2 }}>
                      {parseFloat(a.distancia_km).toFixed(1)} km · {parseFloat(a.frp).toFixed(0)} MW
                    </div>
                    <LinkMaps latitud={a.latitud} longitud={a.longitud} color={dm.accent} />
                    <div style={{ fontSize: 10, color: dm.muted, marginTop: 2 }}>
                      {new Date(a.creado_en).toLocaleString('es-CL')}
                      {a.enviado_whatsapp && ' · WA enviado'}
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}